import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { parseFilters } from '@/composables/useDataExplorerRoute'

/** Human-readable route context for the agent system prompt. */
export function useRouteContext() {
  const route = useRoute()

  return computed(() => {
    const name = String(route.name ?? '')
    const params = route.params

    if (name === 'task-editor' || name === 'task-new') {
      return name === 'task-new'
        ? 'Task editor — creating a new task'
        : `Task editor — editing task ${params.id}`
    }
    if (name === 'task-detail') return `Task detail page — task ${params.id}`
    if (name === 'run-detail') return `Run detail page — run ${params.id}`
    if (name === 'job-detail') return `Job detail page — job ${params.id}`
    if (name === 'job-run-detail') return `Job run detail page — job run ${params.id}`
    if (name === 'job-template-detail') return `Job template detail — template ${params.id}`
    if (name === 'connectors') return 'Connectors list'
    if (name === 'triggers') return 'Triggers list'
    if (name === 'tasks') return 'Tasks list'
    if (name === 'runs') return 'Runs list'
    if (name === 'jobs') return 'Jobs list'
    if (name === 'data-explorer') {
      const parts: string[] = [params.connectorId ? `Data Explorer — connector ${params.connectorId}` : 'Data Explorer']
      const resource = route.query.resource ? String(route.query.resource) : null
      if (resource) parts.push(`resource: ${resource}`)
      const mode = route.query.mode ? String(route.query.mode) : 'visual'
      parts.push(`mode: ${mode}`)
      const limit = route.query.limit ? String(route.query.limit) : '100'
      if (limit !== '100') parts.push(`limit: ${limit}`)
      const filters = parseFilters(route.query.filters)
      if (filters.length) {
        const summary = filters
          .map(f => `${f.field} ${f.operator} ${f.value || '—'}`)
          .join('; ')
        parts.push(`filters: ${summary.length > 200 ? `${summary.slice(0, 197)}…` : summary}`)
      }
      const columns = typeof route.query.columns === 'string' ? route.query.columns : ''
      if (columns) parts.push(`columns: ${columns}`)
      if (route.query.raw) parts.push('raw query: set')
      return parts.join(' · ')
    }
    if (name === 'dashboard') return 'Home dashboard'
    if (name === 'dashboard-detail') return `Job dashboard viewer — dashboard ${params.id}`
    if (name === 'dashboard-template-new') {
      return `Dashboard template editor — creating a new template for job template ${params.templateId}`
    }
    if (name === 'dashboard-template-editor') {
      return `Dashboard template editor — editing dashboard template ${params.dashboardTemplateId} (job template ${params.templateId})`
    }
    if (name === 'public-dashboard') return 'Public dashboard share page (end-customer view)'
    return `Page: ${route.path}`
  })
}
